import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Eye, Flame, MessagesSquare, Compass, Star, Send } from "lucide-react";
import axios from "axios";
import Starfield from "@/components/Starfield";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EASE = [0.16, 1, 0.3, 1];

const CHAMBERS = [
  { id: "society", label: "The Society", icon: Eye },
  { id: "salon", label: "The Salon", icon: MessagesSquare },
  { id: "maproom", label: "Map Room", icon: Compass },
  { id: "hearth", label: "The Hearth", icon: Flame },
  { id: "constellation", label: "Constellation", icon: Star },
];

/* per-chamber voice */
const LABELS = {
  salon: {
    titlePh: "Name the conversation…",
    bodyPh: "Open it with a whisper…",
    cta: "Open the Conversation",
    empty: "No conversations yet — open the first one.",
    noun: "whisper",
    back: "Back to the salon",
    replyPh: "Add your voice…",
    replyCta: "Answer",
  },
  maproom: {
    titlePh: "Name the ambition…",
    bodyPh: "Describe it — who might build it with you?…",
    cta: "Let Us Build",
    empty: "The table is bare — lay the first ambition upon it.",
    noun: "answer",
    back: "Back to the map room",
    replyPh: "Offer your hand…",
    replyCta: "Offer",
  },
};

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: EASE },
});

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

/* a seeker's mark: gold initial medallion + name + date */
const AuthorLine = ({ name, date }) => (
  <div className="flex items-center gap-3.5">
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#c9a227]/40 bg-[#c9a227]/10">
      <span className="font-serif-engraved gold-engraved-deep text-lg">{name?.[0]?.toUpperCase() || "?"}</span>
    </span>
    <div>
      <p className="font-serif-engraved text-base text-[#fdfbf7]/85">{name}</p>
      <p className="text-[10px] uppercase tracking-[0.25em] text-[#c9a227]/50">{formatDate(date)}</p>
    </div>
  </div>
);

const CandleButton = ({ lit, count, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[10px] uppercase tracking-[0.25em] transition-all duration-300 ${
      lit
        ? "border-[#c9a227]/60 bg-[#c9a227]/15 text-[#e8c95a]"
        : "border-[#c9a227]/20 text-[#fdfbf7]/40 hover:border-[#c9a227]/45 hover:text-[#e8c95a]"
    }`}
  >
    <Flame size={13} strokeWidth={1.5} className={lit ? "fill-[#c9a227]/60" : ""} />
    {lit ? "Candle lit" : "Light a candle"}
    {count > 0 ? ` · ${count}` : ""}
  </button>
);

/* the stories row — every seeker a ring of gold, brighter when gathered */
const MemberStories = ({ gatheredIds }) => {
  const [members, setMembers] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/society/members`, { withCredentials: true })
      .then((r) => setMembers(r.data.members))
      .catch(() => {});
  }, []);

  if (members.length === 0) return null;

  return (
    <div data-testid="member-stories" className="mt-8 flex gap-5 overflow-x-auto px-1 pb-2">
      {members.map((m, i) => {
        const gathered = gatheredIds.has(m.id);
        return (
          <div key={`${m.id}-${i}`} data-testid={`story-${i}`} className="flex shrink-0 flex-col items-center gap-2">
            <span
              className={`rounded-full p-[2.5px] transition-all duration-700 ${
                gathered
                  ? "bg-gradient-to-br from-[#e8c95a] via-[#c9a227] to-[#e8c95a] shadow-[0_0_18px_rgba(232,201,90,0.5)]"
                  : "bg-gradient-to-br from-[#c9a227]/70 via-[#c9a227]/40 to-[#7a5f16]/70"
              }`}
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#0a120b]">
                <span className="font-serif-engraved gold-engraved-deep text-2xl">{m.name?.[0]?.toUpperCase() || "?"}</span>
              </span>
            </span>
            <span className="max-w-[68px] truncate text-[9px] uppercase tracking-[0.15em] text-[#fdfbf7]/50">
              {m.name.split(" ")[0]}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/* one conversation in the feed — an Instagram-style card */
const ConversationCard = ({ post, noun, onOpen, onCandle, testid }) => (
  <motion.article
    data-testid={testid}
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, ease: EASE }}
    onClick={() => onOpen(post.id)}
    className="group cursor-pointer rounded-[1.75rem] border border-[#c9a227]/15 bg-[#0b120c]/80 text-left backdrop-blur-sm transition-colors duration-500 hover:border-[#c9a227]/40"
  >
    <div className="px-6 pt-5">
      <AuthorLine name={post.author_name} date={post.created_at} />
    </div>
    <div className="px-6 pb-2 pt-4">
      <h3 className="font-serif-engraved gold-engraved-deep text-lg uppercase tracking-[0.08em]">{post.title}</h3>
      <p className="mt-2 line-clamp-3 text-sm font-light leading-relaxed text-[#fdfbf7]/60">{post.body}</p>
    </div>
    <div className="flex items-center justify-between gap-2 border-t border-[#c9a227]/10 px-6 py-3.5">
      <CandleButton
        lit={post.lit}
        count={post.candles}
        testid={`${testid}-candle`}
        onClick={(e) => {
          e.stopPropagation();
          onCandle(post.id);
        }}
      />
      <span
        data-testid={`${testid}-replies`}
        className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#c9a227]/50"
      >
        <MessagesSquare size={13} strokeWidth={1.5} />
        {post.reply_count || 0} {noun}
        {(post.reply_count || 0) === 1 ? "" : "s"}
      </span>
    </div>
  </motion.article>
);

/* an open conversation: opening whisper + threaded voices */
const Thread = ({ chamber, id, L, onBack }) => {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    axios
      .get(`${API}/society/feed/${id}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData({ post: null, replies: [] }));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const candle = async () => {
    setData((d) =>
      d && d.post
        ? { ...d, post: { ...d.post, lit: !d.post.lit, candles: Math.max(0, d.post.candles + (d.post.lit ? -1 : 1)) } }
        : d
    );
    try {
      await axios.post(`${API}/society/feed/${id}/candle`, {}, { withCredentials: true });
    } catch {
      load();
    }
  };

  const answer = async (e) => {
    e.preventDefault();
    if (!reply.trim() || sending) return;
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/society/feed/${id}/replies`, { body: reply.trim() }, { withCredentials: true });
      setReply("");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "The words did not land. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="w-full" data-testid={`thread-${chamber}`}>
      <button
        data-testid={`thread-back-${chamber}`}
        onClick={onBack}
        className="group inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#fdfbf7]/50 transition-colors duration-300 hover:text-[#e8c95a]"
      >
        <ArrowLeft size={14} strokeWidth={1.5} className="transition-transform duration-500 group-hover:-translate-x-1" />
        {L.back}
      </button>

      {data === null ? (
        <p className="mt-10 text-sm font-light italic text-[#fdfbf7]/35">Unrolling the scroll…</p>
      ) : !data.post ? (
        <p className="mt-10 text-sm font-light italic text-[#fdfbf7]/40">This conversation has faded.</p>
      ) : (
        <>
          <article
            data-testid="thread-post"
            className="mt-8 rounded-[1.75rem] border border-[#c9a227]/30 bg-[#0b120c]/85 px-6 py-7 text-left backdrop-blur-sm"
          >
            <AuthorLine name={data.post.author_name} date={data.post.created_at} />
            <h3 className="font-serif-engraved gold-engraved mt-5 text-xl uppercase tracking-[0.08em] md:text-2xl">
              {data.post.title}
            </h3>
            <p className="mt-4 whitespace-pre-line text-sm font-light leading-relaxed text-[#fdfbf7]/75">
              {data.post.body}
            </p>
            <div className="mt-5">
              <CandleButton lit={data.post.lit} count={data.post.candles} onClick={candle} testid="thread-candle" />
            </div>
          </article>

          <div data-testid={`reply-list-${chamber}`} className="mt-6 flex flex-col gap-4">
            {data.replies.length === 0 ? (
              <p className="text-left text-sm font-light italic text-[#fdfbf7]/40">
                No voices yet — yours could be the first.
              </p>
            ) : (
              data.replies.map((r) => (
                <motion.div
                  key={r.id}
                  data-testid={`reply-${r.id}`}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="rounded-3xl border border-[#c9a227]/12 bg-[#0b120c]/60 px-5 py-5 text-left"
                >
                  <AuthorLine name={r.author_name} date={r.created_at} />
                  <p className="mt-3 whitespace-pre-line text-sm font-light leading-relaxed text-[#fdfbf7]/70">
                    {r.body}
                  </p>
                </motion.div>
              ))
            )}
          </div>

          <form data-testid={`reply-form-${chamber}`} onSubmit={answer} className="mt-6 flex flex-col gap-4">
            <textarea
              data-testid={`reply-input-${chamber}`}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder={L.replyPh}
              rows={2}
              maxLength={600}
              className="w-full resize-none rounded-3xl border border-[#c9a227]/20 bg-[#0b120c]/70 px-5 py-4 text-sm font-light leading-relaxed text-[#fdfbf7] transition-colors duration-300 placeholder:italic placeholder:text-[#fdfbf7]/30 focus:border-[#c9a227]/70 focus:outline-none"
            />
            {error && (
              <p data-testid={`reply-error-${chamber}`} className="text-sm font-light italic text-[#e08a8a]">
                {error}
              </p>
            )}
            <button
              data-testid={`reply-submit-${chamber}`}
              type="submit"
              disabled={sending || !reply.trim()}
              className="group relative self-center overflow-hidden rounded-full border border-[#c9a227]/50 px-10 py-3 text-[11px] uppercase tracking-[0.32em] text-[#e8c95a] transition-colors duration-500 hover:text-[#050a06] disabled:opacity-40"
            >
              <span className="absolute inset-0 -translate-x-full bg-[#c9a227] transition-transform duration-500 ease-out group-hover:translate-x-0" />
              <span className="relative">{sending ? "Sealing…" : L.replyCta}</span>
            </button>
          </form>
        </>
      )}
    </div>
  );
};

/* the forum shared by the salon and the map room */
const ChamberFeed = ({ chamber }) => {
  const L = LABELS[chamber];
  const [posts, setPosts] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [threadId, setThreadId] = useState(null);

  const load = useCallback(() => {
    axios
      .get(`${API}/society/feed`, { params: { chamber }, withCredentials: true })
      .then((r) => setPosts(r.data.posts))
      .catch(() => setPosts([]));
  }, [chamber]);

  useEffect(() => {
    load();
  }, [load]);

  const speak = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim() || posting) return;
    setPosting(true);
    setError("");
    try {
      await axios.post(
        `${API}/society/feed`,
        { chamber, title: title.trim(), body: body.trim() },
        { withCredentials: true }
      );
      setTitle("");
      setBody("");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "The words did not land. Try again.");
    } finally {
      setPosting(false);
    }
  };

  const candle = async (id) => {
    setPosts((ps) =>
      ps ? ps.map((p) => (p.id === id ? { ...p, lit: !p.lit, candles: Math.max(0, p.candles + (p.lit ? -1 : 1)) } : p)) : ps
    );
    try {
      await axios.post(`${API}/society/feed/${id}/candle`, {}, { withCredentials: true });
    } catch {
      load();
    }
  };

  if (threadId) {
    return (
      <div className="mt-6 w-full">
        <Thread chamber={chamber} id={threadId} L={L} onBack={() => { setThreadId(null); load(); }} />
      </div>
    );
  }

  return (
    <div className="mt-6 w-full">
      <form
        data-testid={`feed-form-${chamber}`}
        onSubmit={speak}
        className="flex flex-col gap-3 rounded-[1.75rem] border border-[#c9a227]/15 bg-[#0b120c]/70 p-4 backdrop-blur-sm"
      >
        <input
          data-testid={`feed-title-${chamber}`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={L.titlePh}
          maxLength={120}
          className="w-full rounded-2xl border border-[#c9a227]/20 bg-black/40 px-5 py-3.5 text-center font-serif-engraved text-base tracking-wide text-[#fdfbf7] transition-colors duration-300 placeholder:italic placeholder:text-[#fdfbf7]/30 focus:border-[#c9a227]/70 focus:outline-none"
        />
        <textarea
          data-testid={`feed-input-${chamber}`}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={L.bodyPh}
          rows={3}
          maxLength={600}
          className="w-full resize-none rounded-2xl border border-[#c9a227]/20 bg-black/40 px-5 py-4 text-sm font-light leading-relaxed text-[#fdfbf7] transition-colors duration-300 placeholder:italic placeholder:text-[#fdfbf7]/30 focus:border-[#c9a227]/70 focus:outline-none"
        />
        {error && (
          <p data-testid={`feed-error-${chamber}`} className="text-sm font-light italic text-[#e08a8a]">
            {error}
          </p>
        )}
        <button
          data-testid={`feed-submit-${chamber}`}
          type="submit"
          disabled={posting || !title.trim() || !body.trim()}
          className="group relative self-center overflow-hidden rounded-full border border-[#c9a227]/50 px-10 py-3 text-[11px] uppercase tracking-[0.32em] text-[#e8c95a] transition-colors duration-500 hover:text-[#050a06] disabled:opacity-40"
        >
          <span className="absolute inset-0 -translate-x-full bg-[#c9a227] transition-transform duration-500 ease-out group-hover:translate-x-0" />
          <span className="relative">{posting ? "Sealing…" : L.cta}</span>
        </button>
      </form>

      <div data-testid={`feed-list-${chamber}`} className="mt-6 flex flex-col gap-4">
        {posts === null ? (
          <p className="text-sm font-light italic text-[#fdfbf7]/35">Listening…</p>
        ) : posts.length === 0 ? (
          <p data-testid={`feed-empty-${chamber}`} className="text-sm font-light italic text-[#fdfbf7]/40">
            {L.empty}
          </p>
        ) : (
          posts.map((p) => (
            <ConversationCard
              key={p.id}
              post={p}
              noun={L.noun}
              onOpen={setThreadId}
              onCandle={candle}
              testid={`feed-post-${chamber}-${p.id}`}
            />
          ))
        )}
      </div>
    </div>
  );
};

/* chamber iv — the hearth: live words spoken by those gathered now */
const HearthChamber = ({ me }) => {
  const [messages, setMessages] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(() => {
    axios
      .get(`${API}/society/chat`, { withCredentials: true })
      .then((r) => setMessages(r.data.messages))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/society/chat`, { body: body.trim() }, { withCredentials: true });
      setBody("");
      load();
    } catch {
      /* the hearth stays warm; try again */
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex w-full flex-col items-center" data-testid="hearth-chamber">
      <p className="font-serif-engraved gold-engraved-deep text-lg uppercase tracking-[0.22em]">The Hearth</p>
      <p className="mt-3 max-w-md text-sm font-light leading-loose text-[#fdfbf7]/60">
        Words spoken here are alive — they belong to whoever is gathered in this moment.
      </p>

      <div
        data-testid="hearth-messages"
        className="mt-6 flex max-h-[46vh] w-full flex-col gap-3 overflow-y-auto rounded-[1.75rem] border border-[#c9a227]/15 bg-[#0b120c]/60 p-4 backdrop-blur-sm"
      >
        {messages === null ? (
          <p className="py-8 text-sm font-light italic text-[#fdfbf7]/35">Kindling the fire…</p>
        ) : messages.length === 0 ? (
          <p data-testid="hearth-empty" className="py-8 text-sm font-light italic text-[#fdfbf7]/40">
            The hearth is quiet — speak the first word.
          </p>
        ) : (
          messages.map((m) => {
            const own = me && m.author_id === me.id;
            return (
              <div
                key={m.id}
                data-testid={`hearth-message-${m.id}`}
                className={`flex flex-col ${own ? "items-end" : "items-start"}`}
              >
                <span className="mb-1 px-2 text-[9px] uppercase tracking-[0.2em] text-[#c9a227]/50">
                  {m.author_name} · {formatTime(m.created_at)}
                </span>
                <span
                  className={`max-w-[85%] rounded-3xl px-4 py-2.5 text-left text-sm font-light leading-relaxed ${
                    own
                      ? "rounded-br-md border border-[#c9a227]/40 bg-[#c9a227]/15 text-[#fdfbf7]/90"
                      : "rounded-bl-md border border-[#c9a227]/12 bg-black/50 text-[#fdfbf7]/75"
                  }`}
                >
                  {m.body}
                </span>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <form data-testid="hearth-form" onSubmit={send} className="mt-4 flex w-full items-center gap-2.5">
        <input
          data-testid="hearth-input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Speak into the hearth…"
          maxLength={500}
          className="flex-1 rounded-full border border-[#c9a227]/20 bg-[#0b120c]/70 px-5 py-3.5 text-sm font-light text-[#fdfbf7] transition-colors duration-300 placeholder:italic placeholder:text-[#fdfbf7]/30 focus:border-[#c9a227]/70 focus:outline-none"
        />
        <button
          data-testid="hearth-submit"
          type="submit"
          disabled={sending || !body.trim()}
          aria-label="Speak"
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#c9a227]/50 text-[#e8c95a] transition-all duration-300 hover:bg-[#c9a227]/20 disabled:opacity-40"
        >
          <Send size={16} strokeWidth={1.5} />
        </button>
      </form>
    </div>
  );
};

/* chamber i — the society itself */
const SocietyChamber = () => (
  <div className="flex flex-col items-center">
    <motion.div
      {...fade(0.05)}
      className="w-full rounded-[1.75rem] border border-[#c9a227]/15 bg-[#0b120c]/70 px-7 py-10 text-center backdrop-blur-sm"
    >
      <p className="text-[10px] uppercase tracking-[0.45em] text-[#c9a227]/70">What is Celestial Society?</p>
      <p className="mx-auto mt-7 max-w-xl text-base font-light leading-loose text-[#fdfbf7]/65">
        Celestial Society is a modern-day salon for like-minded seekers — a place
        to step away from the noise of the outside world and find the people,
        conversations and ideas that make you feel at home.
      </p>
      <p className="font-serif-engraved mt-10 text-lg font-light italic text-[#e8c95a]/85 md:text-xl">
        Come for the conversation. Stay for the people. Build something together.
      </p>
    </motion.div>
  </div>
);

/* chamber ii — the salon: conversations + the first gathering */
const SalonChamber = () => (
  <div className="flex flex-col items-center">
    <motion.div
      {...fade(0.05)}
      className="w-full rounded-[1.75rem] border border-[#c9a227]/15 bg-[#0b120c]/70 px-7 py-9 text-center backdrop-blur-sm"
    >
      <p className="font-serif-engraved gold-engraved-deep text-lg uppercase tracking-[0.22em]">Our Salon</p>
      <p className="mx-auto mt-4 max-w-xl text-sm font-light leading-loose text-[#fdfbf7]/60">
        Our Salon is where we come together to discuss philosophy, culture, art,
        society and the questions shaping our time.
      </p>
      <div className="mt-8 flex items-center justify-center gap-4" aria-hidden="true">
        <span className="h-px w-14 bg-gradient-to-r from-transparent to-[#c9a227]/60" />
        <span className="block h-1.5 w-1.5 rotate-45 border border-[#c9a227]/70" />
        <span className="h-px w-14 bg-gradient-to-l from-transparent to-[#c9a227]/60" />
      </div>
      <p className="mt-6 text-[10px] uppercase tracking-[0.45em] text-[#c9a227]/70">The First Salon Convenes</p>
      <p
        data-testid="society-salon-teaser-date"
        className="font-serif-engraved gold-engraved mt-4 text-4xl font-semibold uppercase leading-tight tracking-[0.1em] md:text-5xl"
      >
        24 September
      </p>
      <p data-testid="society-salon-teaser-venue" className="mt-4 text-sm font-light tracking-wide text-[#fdfbf7]/60">
        within <span className="font-serif-engraved gold-engraved-deep uppercase tracking-[0.2em]">The Salon</span>
        <span className="text-[#fdfbf7]/40"> — our gathering hall, online</span>
      </p>
      <motion.span
        animate={{ opacity: [0.35, 0.75, 0.35] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="mt-4 block text-xs font-light italic text-[#fdfbf7]/45"
        data-testid="society-salon-coming-soon"
      >
        the hour remains veiled · the way in arrives by sealed letter
      </motion.span>
    </motion.div>

    <motion.div {...fade(0.2)} className="w-full">
      <ChamberFeed chamber="salon" />
    </motion.div>
  </div>
);

/* chamber iii — the map room: collaboration */
const MapRoomChamber = () => (
  <div className="flex flex-col items-center">
    <motion.div
      {...fade(0.05)}
      className="w-full rounded-[1.75rem] border border-[#c9a227]/15 bg-[#0b120c]/70 px-7 py-9 text-center backdrop-blur-sm"
    >
      <p className="font-serif-engraved gold-engraved-deep text-lg uppercase tracking-[0.22em]">Our Map Room</p>
      <p className="mx-auto mt-4 max-w-xl text-sm font-light leading-loose text-[#fdfbf7]/60">
        Our Map Room is where conversation becomes collaboration. It is a place to
        bring ideas, creative projects and ambitions to the table, find people who
        can help bring them to life, and help one another build something
        extraordinary.
      </p>
    </motion.div>
    <motion.div {...fade(0.2)} className="w-full">
      <ChamberFeed chamber="maproom" />
    </motion.div>
  </div>
);

/* chamber v — the constellation: every seeker is a star */
const ConstellationChamber = () => {
  const [members, setMembers] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/society/members`, { withCredentials: true })
      .then((r) => setMembers(r.data.members))
      .catch(() => setMembers([]));
  }, []);

  return (
    <div className="flex flex-col items-center">
      <motion.p {...fade(0.05)} className="font-serif-engraved gold-engraved-deep text-lg uppercase tracking-[0.22em]">
        The Constellation
      </motion.p>
      <motion.p {...fade(0.15)} className="mt-4 max-w-xl text-sm font-light leading-loose text-[#fdfbf7]/60">
        Every seeker who has taken the oath burns here as a star. Names only —
        the society keeps its own confidence.
      </motion.p>
      <div data-testid="members-list" className="mt-8 grid w-full gap-3 sm:grid-cols-2">
        {members === null ? (
          <p className="col-span-full text-sm font-light italic text-[#fdfbf7]/35">Reading the sky…</p>
        ) : members.length === 0 ? (
          <p className="col-span-full text-sm font-light italic text-[#fdfbf7]/40">The sky awaits its first stars.</p>
        ) : (
          members.map((m, i) => (
            <motion.div
              key={`${m.name}-${i}`}
              data-testid={`member-star-${i}`}
              {...fade(0.2 + Math.min(i, 8) * 0.06)}
              className="flex items-center gap-4 rounded-3xl border border-[#c9a227]/12 bg-[#0b120c]/60 px-6 py-4 text-left"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#c9a227]/35 bg-[#c9a227]/10">
                <span className="font-serif-engraved gold-engraved-deep text-base">
                  {m.name?.[0]?.toUpperCase() || "?"}
                </span>
              </span>
              <div>
                <p className="font-serif-engraved text-base text-[#fdfbf7]/85">{m.name}</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#c9a227]/45">
                  seeker since {formatDate(m.joined_at)}
                </p>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

const SocietyPage = () => {
  const [member, setMember] = useState(null);
  const [allowed, setAllowed] = useState(null);
  const [chamber, setChamber] = useState("society");
  const [gathered, setGathered] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        setMember(r.data);
        setAllowed(true);
      })
      .catch(() => setAllowed(false));
  }, []);

  /* presence — the society knows who is gathered */
  useEffect(() => {
    if (!allowed) return undefined;
    const beat = () => {
      axios
        .post(`${API}/society/presence`, {}, { withCredentials: true })
        .then(() => axios.get(`${API}/society/presence`, { withCredentials: true }))
        .then((r) => setGathered(r.data.gathered))
        .catch(() => {});
    };
    beat();
    const t = setInterval(beat, 25000);
    window.addEventListener("focus", beat);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", beat);
    };
  }, [allowed]);

  if (allowed === null) return <main className="min-h-screen bg-[#060b07]" />;

  /* the chamber is sealed to those who have not taken the oath */
  if (!allowed) {
    return (
      <main
        data-testid="society-gate"
        className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#060b07] px-6 text-center"
      >
        <Starfield density={60} />
        <Eye className="relative z-10 text-[#c9a227]/70" size={30} strokeWidth={1.2} />
        <p className="font-serif-engraved relative z-10 mt-6 text-2xl text-[#fdfbf7]/80 md:text-3xl">
          This chamber opens only to seekers.
        </p>
        <Link
          to="/"
          data-testid="society-gate-home-link"
          className="relative z-10 mt-8 border border-[#c9a227]/40 px-7 py-3 text-[11px] uppercase tracking-[0.3em] text-[#e8c95a] transition-colors duration-300 hover:bg-[#c9a227]/10"
        >
          Return to the universe
        </Link>
      </main>
    );
  }

  const gatheredNames = new Set(gathered.map((g) => g.member_id));

  return (
    <main data-testid="society-page" className="relative min-h-screen overflow-hidden bg-[#060b07]">
      {/* deep dark sky — no hall, only stars and a breath of gold */}
      <div className="pointer-events-none fixed inset-0" aria-hidden="true">
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(ellipse 70% 40% at 50% 0%, rgba(201,162,39,0.07), transparent 65%)" }}
        />
        <Starfield density={35} />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-2xl px-4 pb-36 md:px-6">
        {/* app header */}
        <motion.header
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="flex items-center justify-between pt-6"
        >
          <Link
            to="/"
            data-testid="society-back-link"
            className="group inline-flex items-center gap-2.5 text-[11px] uppercase tracking-[0.3em] text-[#fdfbf7]/50 transition-colors duration-300 hover:text-[#e8c95a]"
          >
            <ArrowLeft size={14} strokeWidth={1.5} className="transition-transform duration-500 group-hover:-translate-x-1" />
            The universe
          </Link>
          <img
            src="/assets/logo_transparent.webp"
            alt="Celestial Studios"
            data-testid="society-logo"
            className="h-12 w-auto opacity-90"
          />
          <div
            data-testid="gathered-now"
            className="flex items-center gap-2 rounded-full border border-[#c9a227]/25 bg-black/50 px-3.5 py-1.5"
          >
            <motion.span
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              className="h-1.5 w-1.5 rounded-full bg-[#e8c95a]"
            />
            <span className="text-[9px] uppercase tracking-[0.2em] text-[#c9a227]/80">
              {gathered.length} gathered
            </span>
          </div>
        </motion.header>

        {/* the house of the society */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 1, ease: EASE }}
          className="mt-7 text-center"
        >
          <p data-testid="society-eyebrow" className="text-[9px] uppercase tracking-[0.45em] text-[#c9a227]/70">
            You have taken the oath
          </p>
          <h1
            data-testid="society-title"
            className="font-serif-engraved gold-engraved mt-3 text-3xl font-semibold uppercase tracking-[0.12em] md:text-4xl"
          >
            The Celestial Society
          </h1>
          <p data-testid="society-motto" className="font-serif-engraved mt-3 text-base font-light italic text-[#fdfbf7]/65">
            “The Courage to Think Differently.”
          </p>
          {member?.name && (
            <p data-testid="society-member-name" className="mt-3 text-[10px] uppercase tracking-[0.35em] text-[#c9a227]/60">
              Seeker {member.name}
            </p>
          )}
        </motion.div>

        {/* the constellation as a stories row */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 1 }}>
          <MemberStories gatheredIds={gatheredNames} />
        </motion.div>

        {/* the open chamber */}
        <AnimatePresence mode="wait">
          <motion.div
            key={chamber}
            data-testid={`society-chamber-${chamber}`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.55, ease: EASE }}
            className="mt-7 text-center"
          >
            {chamber === "society" && <SocietyChamber />}
            {chamber === "salon" && <SalonChamber />}
            {chamber === "maproom" && <MapRoomChamber />}
            {chamber === "hearth" && <HearthChamber me={member} />}
            {chamber === "constellation" && <ConstellationChamber />}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* the floating chamber bar — app-style bottom navigation */}
      <motion.nav
        data-testid="society-chambers"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
        className="fixed inset-x-0 bottom-4 z-40 flex justify-center px-4"
      >
        <div className="flex items-center gap-1 rounded-full border border-[#c9a227]/20 bg-[#0a0f0a]/90 p-1.5 shadow-[0_10px_40px_rgba(0,0,0,0.65)] backdrop-blur-xl">
          {CHAMBERS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`society-nav-${id}`}
              onClick={() => setChamber(id)}
              aria-label={label}
              className={`flex items-center gap-2 rounded-full px-3.5 py-2.5 transition-all duration-500 ${
                chamber === id
                  ? "bg-[#c9a227]/25 text-[#e8c95a] shadow-[0_0_18px_rgba(201,162,39,0.25)]"
                  : "text-[#fdfbf7]/45 hover:text-[#fdfbf7]/80"
              }`}
            >
              <Icon size={16} strokeWidth={1.4} />
              {chamber === id && (
                <span className="text-[9px] uppercase tracking-[0.2em]">{label}</span>
              )}
            </button>
          ))}
        </div>
      </motion.nav>
    </main>
  );
};

export default SocietyPage;
